import React, { useCallback } from 'react';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import { Box, FormHelperText, Typography, useTheme } from '@mui/material';
import { MarkdownEditorProps } from './types';
import { defaultEditorConfig, defaultPlugins } from './config';
import { useMarkdownParser } from './useMarkdownParser';
import { getEditorContainerStyles, getLabelStyles, getHelperTextStyles } from './styles';
import { useAppSelector } from '@shared/store';
import { RootState } from '@shared/store';

export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  label,
  error = false,
  helperText,
  required = false,
  height = 600,
  disabled = false,
}) => {
  const theme = useTheme();
  const mdParser = useMarkdownParser();
  const customizer = useAppSelector((state: RootState) => state.customizer);
  const direction = customizer.activeDir || 'ltr';

  const handleEditorChange = useCallback(
    ({ text }: { text: string }) => {
      onChange(text);
    },
    [onChange],
  );

  const renderHTML = useCallback(
    (text: string) => {
      return mdParser.render(text);
    },
    [mdParser],
  );

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {label && (
        <Typography variant="body2" sx={getLabelStyles(error)}>
          {label}
          {required && ' *'}
        </Typography>
      )}

      <Box
        className="markdown-editor-root"
        data-direction={direction}
        sx={getEditorContainerStyles(theme, error, height)}>
        <MdEditor
          value={value}
          style={{
            height: `${height}px`,
          }}
          renderHTML={renderHTML}
          onChange={handleEditorChange}
          config={defaultEditorConfig}
          plugins={defaultPlugins}
          readOnly={disabled}
        />
      </Box>

      {helperText && (
        <FormHelperText error={error} sx={getHelperTextStyles()}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};
